import React, { Component } from 'react';
import { fetchApi } from '../../Network/NetworkService';
import '../../Assets/Pages/adminSuiviLists.css';

// Configuration des icônes zmdi selon l'évolution de la santé
const healthEvolutionConfig = {
    "Je vais très bien": { icon: 'zmdi-check-circle', cls: "green" },
    "J'ai encore quelques interrogations": { icon: 'zmdi-mood', cls: "yellow" },
    "Certains symptômes persistent": { icon: 'zmdi-mood-bad', cls: "orange" },
    "Ma situation s'est aggravée": { icon: 'zmdi-alert-triangle', cls: "red" },
};

// Configuration des types de besoin
const needTypeLabels = {
    "accompagne": "Accompagnement",
    "nouvelle_question": "Nouvelle question",
    "plus_besoin": "Plus besoin",
};

// Configuration des actions de redirection
const redirectActionLabels = {
    "subscription": "Abonnement",
    "new_consultation": "Nouvelle consultation",
    "completed": "Terminé",
};

const MONTHS_FR = [
    "janv.", "févr.", "mars", "avr.", "mai", "juin",
    "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }

function patientName(s) {
    if (!s.userId) return "Patient inconnu";
    return `${s.userId.FName ?? ""} ${s.userId.LName ?? ""}`.trim() || "Patient inconnu";
}

function initials(f, l) {
    return `${(f?.[0] ?? "?").toUpperCase()}${(l?.[0] ?? "").toUpperCase()}`;
}

// Stat Component
class Stat extends Component {
    render() {
        const { label, value, color } = this.props;
        return (
            <div className="admin-sd-stat" style={{ "--accent": color }}>
                <p className="admin-sd-stat-label">{label}</p>
                <p className="admin-sd-stat-value">{value}</p>
            </div>
        );
    }
}

// Provenance du suivi : utm_source enregistré à la soumission du formulaire.
// "whatsapp" = lien généré depuis l'admin ; null = parcours normal (naturel).
class SourcePill extends Component {
    render() {
        const { source } = this.props;
        if (source === "whatsapp") {
            return (
                <span className="admin-sd-pill whatsapp">
                    <i className="zmdi zmdi-whatsapp" />
                    WhatsApp
                </span>
            );
        }
        if (source) {
            return <span className="admin-sd-pill gray">{source}</span>;
        }
        return <span className="admin-sd-pill gray">Naturel</span>;
    }
}

export class SuiviPathologieTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            loading: true,
            error: null,
            mounted: false,
            search: "",
            searchBy: "patientName",
            sort: "desc",
            filterEvolution: "all",
            filterNeedType: "all",
            filterSource: "all",
            page: 1,
        };

        this.pageSize = 8;
    }

    componentDidMount() {
        this.setState({ mounted: true });
        this._fetchSuivis();
    }

    _fetchSuivis() {
        this.setState({ loading: true, error: null });

        fetchApi('/suivi/pathologie', { method: 'GET' })
            .then(res => {
                this.setState({ data: res.data || [], loading: false });
            })
            .catch(err => {
                console.error('Error fetching suivi-pathologie:', err);
                this.setState({ error: err, loading: false });
            });
    }

    _getFiltered() {
        const { data, filterEvolution, filterNeedType, filterSource, search, searchBy, sort } = this.state;

        let rows = [...data];

        if (filterEvolution !== "all") {
            rows = rows.filter(r => r.q1_healthEvolution === filterEvolution);
        }

        if (filterNeedType !== "all") {
            rows = rows.filter(r => r.q2_needType === filterNeedType);
        }

        // Provenance : suivis remplis depuis le lien envoyé au patient
        // (utm_source=whatsapp posé par le générateur de lien de l'admin).
        if (filterSource !== "all") {
            rows = rows.filter(r =>
                filterSource === "whatsapp" ? r.source === "whatsapp" : r.source !== "whatsapp"
            );
        }

        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(r =>
                searchBy === "patientName"
                    ? patientName(r).toLowerCase().includes(q)
                    : (r.q1_healthEvolution || "").toLowerCase().includes(q)
            );
        }

        rows.sort((a, b) => {
            const da = new Date(a.createdAt).getTime();
            const db = new Date(b.createdAt).getTime();
            return sort === "desc" ? db - da : da - db;
        });

        return rows;
    }

    _getStats() {
        const { data } = this.state;
        return {
            total: data.length,
            attention: data.filter(d => d.requiresAttention).length,
            subscription: data.filter(d => d.redirectAction === 'subscription').length,
            newConsultation: data.filter(d => d.redirectAction === 'new_consultation').length,
            // Suivis obtenus via le lien WhatsApp généré depuis l'admin
            // (/suivis/lien-whatsapp) — même compteur que la page dermato.
            viaLink: data.filter(d => d.source === 'whatsapp').length,
        };
    }

    render() {
        const { mounted, search, searchBy, sort, filterEvolution, filterNeedType, filterSource, page, loading, error } = this.state;
        const filtered = this._getFiltered();
        const stats = this._getStats();

        const totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
        const currentPage = Math.min(page, totalPages);
        const paginated = filtered.slice(
            (currentPage - 1) * this.pageSize,
            currentPage * this.pageSize,
        );

        return (
            <div className="admin-sd-root">
                <div className="admin-sd-container">
                    {/* Header */}
                    <div className="admin-sd-header">
                        <div>
                            <h1>Suivis médicaux</h1>
                            <p>Consultez l'évolution de santé de vos patients et leurs besoins.</p>
                        </div>
                        <span className="admin-sd-header-badge">{this.state.data.length} suivis</span>
                    </div>

                    {/* Stats */}
                    <div className="admin-sd-stats">
                        <Stat label="Total" value={stats.total} color="#4e95fb" />
                        <Stat label="Abonnement" value={stats.subscription} color="#02ce26" />
                        <Stat label="Nouvelle consult." value={stats.newConsultation} color="#2b7cd1" />
                        <Stat label="Attention requise" value={stats.attention} color="#ff4858" />
                        <Stat label="Via lien WhatsApp" value={stats.viaLink} color="#25D366" />
                    </div>

                    {/* Toolbar */}
                    <div className="admin-sd-toolbar">
                        <div className="admin-sd-search">
                            <i className="zmdi zmdi-search" />
                            <input
                                className="admin-sd-input"
                                placeholder={
                                    searchBy === "patientName"
                                        ? "Rechercher un patient..."
                                        : "Rechercher une évolution..."
                                }
                                value={search}
                                onChange={e => this.setState({ search: e.target.value, page: 1 })}
                            />
                        </div>
                        <select
                            className="admin-sd-select"
                            value={searchBy}
                            onChange={e => this.setState({ searchBy: e.target.value })}
                        >
                            <option value="patientName">Par patient</option>
                            <option value="evolution">Par évolution</option>
                        </select>
                        <select
                            className="admin-sd-select"
                            value={filterEvolution}
                            onChange={e => this.setState({ filterEvolution: e.target.value, page: 1 })}
                        >
                            <option value="all">Toutes les évolutions</option>
                            <option value="Je vais très bien">Très bien</option>
                            <option value="J'ai encore quelques interrogations">Quelques interrogations</option>
                            <option value="Certains symptômes persistent">Symptômes persistants</option>
                            <option value="Ma situation s'est aggravée">Situation aggravée</option>
                        </select>
                        <select
                            className="admin-sd-select"
                            value={filterNeedType}
                            onChange={e => this.setState({ filterNeedType: e.target.value, page: 1 })}
                        >
                            <option value="all">Tous les besoins</option>
                            <option value="accompagne">Accompagnement</option>
                            <option value="nouvelle_question">Nouvelle question</option>
                            <option value="plus_besoin">Plus besoin</option>
                        </select>
                        <select
                            className="admin-sd-select"
                            value={filterSource}
                            onChange={e => this.setState({ filterSource: e.target.value, page: 1 })}
                        >
                            <option value="all">Toutes provenances</option>
                            <option value="whatsapp">Via lien WhatsApp</option>
                            <option value="other">Autres</option>
                        </select>
                        <button
                            className="admin-sd-btn"
                            onClick={() =>
                                this.setState(prev => ({ sort: prev.sort === "desc" ? "asc" : "desc" }))
                            }
                        >
                            <i className={sort === "desc" ? "zmdi zmdi-sort-amount-desc" : "zmdi zmdi-sort-amount-asc"} />
                            {sort === "desc" ? "Plus récents" : "Plus anciens"}
                        </button>
                    </div>

                    {/* Table */}
                    <div className="admin-sd-card">
                        {loading ? (
                            <div className="admin-sd-loader-wrap">
                                <div className="admin-sd-spinner"></div>
                                <p>Chargement des suivis...</p>
                            </div>
                        ) : error ? (
                            <div className="admin-sd-loader-wrap">
                                <i className="zmdi zmdi-alert-triangle" style={{ color: '#ff4858', fontSize: 36 }} />
                                <p>Erreur lors du chargement</p>
                            </div>
                        ) : (
                            <>
                                <div className="admin-sd-table-wrap">
                                    <table className="admin-sd-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 80 }}>Date</th>
                                                <th>Patient</th>
                                                <th>Évolution</th>
                                                <th>Besoin</th>
                                                <th>Médecin</th>
                                                <th>Source</th>
                                                <th>Action</th>
                                                <th className="admin-sd-th-right">Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginated.length === 0 && (
                                                <tr>
                                                    <td colSpan={8} className="admin-sd-empty">
                                                        Aucun suivi ne correspond à votre recherche.
                                                    </td>
                                                </tr>
                                            )}
                                            {paginated.map(s => {
                                                const d = new Date(s.createdAt);
                                                const cfg = healthEvolutionConfig[s.q1_healthEvolution] || {
                                                    icon: 'zmdi-help',
                                                    cls: "gray",
                                                };
                                                return (
                                                    <tr key={s._id}>
                                                        <td>
                                                            <div className="admin-sd-date">
                                                                <span className="admin-sd-date-day">
                                                                    {mounted ? d.getDate() : d.getUTCDate()}
                                                                </span>
                                                                <span className="admin-sd-date-month">
                                                                    {MONTHS_FR[mounted ? d.getMonth() : d.getUTCMonth()]}
                                                                </span>
                                                                <span className="admin-sd-date-time">
                                                                    {mounted
                                                                        ? `${pad(d.getHours())}:${pad(d.getMinutes())}`
                                                                        : `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="admin-sd-patient">
                                                                <div className="admin-sd-avatar">
                                                                    {initials(s.userId?.FName, s.userId?.LName)}
                                                                </div>
                                                                <div className="admin-sd-patient-info">
                                                                    <div className="admin-sd-patient-name">{patientName(s)}</div>
                                                                    <div className="admin-sd-patient-email">
                                                                        {s.userId?.email ?? "—"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`admin-sd-pill ${cfg.cls}`}>
                                                                <i className={`zmdi ${cfg.icon}`} />
                                                                {s.q1_healthEvolution}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className="admin-sd-need-type">
                                                                {needTypeLabels[s.q2_needType] || s.q2_needType}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {s.docteurName ? (
                                                                <span className="admin-sd-doctor" title="Médecin choisi dans le lien envoyé au patient">
                                                                    <i className="zmdi zmdi-account-box" />
                                                                    Dr {s.docteurName}
                                                                </span>
                                                            ) : s.doctorId ? (
                                                                <span className="admin-sd-doctor">
                                                                    <i className="zmdi zmdi-account-box" />
                                                                    Dr {s.doctorId.FName} {s.doctorId.LName}
                                                                </span>
                                                            ) : (
                                                                <span className="admin-sd-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <SourcePill source={s.source} />
                                                        </td>
                                                        <td>
                                                            <span className={`admin-sd-action-pill ${s.redirectAction}`}>
                                                                <i className={
                                                                    s.redirectAction === 'subscription' ? 'zmdi zmdi-calendar-check' :
                                                                        s.redirectAction === 'new_consultation' ? 'zmdi zmdi-hospital' : 'zmdi zmdi-account-box'
                                                                } />
                                                                {redirectActionLabels[s.redirectAction]}
                                                            </span>
                                                        </td>
                                                        <td className="admin-sd-td-right">
                                                            {s.requiresAttention ? (
                                                                <span className="admin-sd-pill red">
                                                                    <i className="zmdi zmdi-alert-triangle" />
                                                                    Attention
                                                                </span>
                                                            ) : (
                                                                <span className="admin-sd-pill gray">
                                                                    <i className="zmdi zmdi-check" />
                                                                    OK
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="admin-sd-pagination">
                                    <span>
                                        Affichant{" "}
                                        <strong>
                                            {filtered.length === 0 ? 0 : (currentPage - 1) * this.pageSize + 1}
                                        </strong>
                                        {" – "}
                                        <strong>{Math.min(currentPage * this.pageSize, filtered.length)}</strong>
                                        {" sur "}
                                        <strong>{filtered.length}</strong>
                                    </span>
                                    <div className="admin-sd-pagination-controls">
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={currentPage === 1}
                                            onClick={() =>
                                                this.setState(prev => ({ page: Math.max(1, prev.page - 1) }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-left" />
                                        </button>
                                        <span className="admin-sd-page-info">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={currentPage === totalPages}
                                            onClick={() =>
                                                this.setState(prev => ({
                                                    page: Math.min(totalPages, prev.page + 1),
                                                }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-right" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SuiviPathologieTable;
